import React, { Fragment, useMemo, useState } from 'react';
import { PageTitle } from '../../../components/PageTitle';
import { useTranslation } from 'react-i18next';

import { useLocation, useNavigate } from 'react-router';
import { ManagedDataTable } from '../../../components/ManagedDataTable';
import {
  ColumnFilters,
  FilterFields,
  IManagedDataTable,
} from '../../../components/ManagedDataTable/interfaces';
import { PlannedActivity } from '../../../api/activitiesApi/interfaces';
import { getColumns, getRowClassName } from './helpers';
import { ROUTES } from '../../../routes/Routes';
import { useAuth } from '../../../context/AuthProvider';
import { DATA_TABLE_STORAGE_KEYS } from '../../../constants';
import { UpsertPlannedActivityDialog } from '../dialogs/UpsertPlannedActivityDialog';
import { FloatingActionsButtons } from '../../../components/FloatingActionsButtons';
import { CreateAction } from '../../../components/FloatingActionsButtons/components/CreateAction';
import {
  useGetActions,
  useGetPlannedActivities,
} from '../../../reactQuery/Activities/activities';
import { useGetGroupList } from '../../../reactQuery/groups';
import { useGetAgentsList } from '../../../reactQuery/Agents/agents';
import { useGetClientsList } from '../../../reactQuery/clients';
import { useGetContactsList } from '../../../reactQuery/contacts';
import { getWarningRowStyling } from '../../../helpers/tableStyleHelper';

export const PlannedActivitiesOverview: React.FC = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { auth } = useAuth();
  const [showCreateDialog, setShowCreateDialog] = useState<boolean>(false);

  const { data: groupList } = useGetGroupList();
  const { data: agentsList } = useGetAgentsList();
  const { data: clientsList } = useGetClientsList();
  const { data: contactsList } = useGetContactsList();
  const { data: actionsList } = useGetActions();

  const isAdmin = auth?.user && auth.user.isAdmin === true;

  const defaultFilter = useMemo((): FilterFields => {
    if (!isAdmin && auth?.user) {
      return [
        {
          field: 'agent',
          operator: '=',
          value: auth.user.id,
        },
      ];
    }

    return [];
  }, [auth?.user]);

  const columnFilters: ColumnFilters = [
    {
      column: 'performAt',
      label: t('entities:plannedActivity.performAt'),
      type: 'date',
    },
    {
      column: 'group',
      label: t('entities:client.group'),
      type: 'multivalue',
      options: groupList || [],
    },
    {
      column: 'client',
      filterName: 'clients',
      label: t('entities:activityLog.clients'),
      type: 'multivalue',
      options: clientsList || [],
    },
    {
      column: 'contacts',
      label: t('entities:activityLog.contacts'),
      type: 'multivalue',
      options:
        contactsList?.map((agent) => {
          return {
            id: agent.id,
            name: `${agent.firstName} ${agent.lastName}`,
          };
        }) || [],
    },
    {
      column: 'action',
      label: t('entities:activityLog.action'),
      type: 'multivalue',
      options: actionsList || [],
    },
  ];

  if (isAdmin) {
    columnFilters.push({
      column: 'agent',
      filterName: 'agents',
      label: t('entities:activityLog.agent'),
      type: 'multivalue',
      options:
        agentsList?.map((agent) => {
          return {
            id: agent.id,
            name: `${agent.firstName} ${agent.lastName}`,
          };
        }) || [],
    });
  }

  const onRowClick = (row: PlannedActivity): void => {
    navigate(ROUTES.activities.details(row.id), { state: { from: location } });
  };

  return (
    <Fragment>
      <PageTitle
        title={t('plannedActivities:title')}
        subTitle={t('plannedActivities:subTitle')}
        space
      />
      <ManagedDataTable
        query={
          useGetPlannedActivities as IManagedDataTable<PlannedActivity>['query']
        }
        columns={getColumns()}
        onRowClick={onRowClick}
        storageKey={DATA_TABLE_STORAGE_KEYS.plannedActivities}
        filter={defaultFilter}
        columnFilters={columnFilters}
        getRowClassName={getRowClassName}
        initialSort={{ field: 'performAt', sort: 'asc' }}
        sx={getWarningRowStyling()}
      />

      <UpsertPlannedActivityDialog
        show={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
      />

      <FloatingActionsButtons>
        <CreateAction onClick={() => setShowCreateDialog(true)} />
      </FloatingActionsButtons>
    </Fragment>
  );
};
